'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  DollarSign,
  PiggyBank,
  CreditCard,
  Target,
  AlertTriangle,
  Info,
  Eye,
  EyeOff
} from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import { BirhausCurrencyDisplay } from './BirhausCurrencyDisplay'
import type { 
  BirhausFinancialMetricCardProps, 
  FinancialMetric,
  MetricTrend,
  MetricTrendDirection,
  MetricCategory
} from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Metric type configuration
const metricTypeConfig: Record<MetricCategory, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  ingresos: {
    icon: <TrendingUp size={20} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Ingresos',
    nameEn: 'Income'
  },
  gastos: {
    icon: <TrendingDown size={20} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Gastos',
    nameEn: 'Expenses'
  },
  balance: {
    icon: <DollarSign size={20} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Balance',
    nameEn: 'Balance'
  },
  proyeccion: {
    icon: <TrendingUp size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Proyección',
    nameEn: 'Projection'
  }
}

// Trend configuration
const trendConfig: Record<MetricTrendDirection, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  subida: {
    icon: <TrendingUp size={16} />,
    colorClass: 'text-green-600',
    nameEs: 'Creciendo',
    nameEn: 'Increasing'
  },
  bajada: {
    icon: <TrendingDown size={16} />,
    colorClass: 'text-red-600',
    nameEs: 'Decreciendo',
    nameEn: 'Decreasing'
  },
  estable: {
    icon: <Minus size={16} />,
    colorClass: 'text-gray-600',
    nameEs: 'Estable',
    nameEn: 'Stable'
  }
}

/**
 * BirhausFinancialMetricCard - Financial KPI display component
 * 
 * Features:
 * - Multiple metric types with appropriate icons and colors
 * - Trend analysis with percentage change indicators
 * - Privacy toggle for sensitive financial information
 * - Goal progress tracking and alerts
 * - Spanish-first metric names and descriptions
 * - Accessibility compliant with proper ARIA labels
 * - Responsive layout for different screen sizes
 * - Warning indicators for concerning metrics
 * - Historical comparison support
 */
export function BirhausFinancialMetricCard({
  metric,
  showTrend = true,
  showGoalProgress = false,
  allowPrivacyToggle = true,
  variant = 'default',
  size = 'medium',
  className,
  ...props
}: BirhausFinancialMetricCardProps) {
  const { language, reportViolation } = useBirhaus()
  const [isPrivacyEnabled, setIsPrivacyEnabled] = React.useState(false)

  // BIRHAUS validation: Check for concerning financial metrics
  React.useEffect(() => {
    // Alert for negative balances
    if (metric.categoria === 'balance' && metric.valor < 0) {
      reportViolation({
        type: 'performance',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Negative balance detected: ${metric.valor}`,
        messageEs: `Saldo negativo detectado: ${metric.valor}`,
        messageEn: `Negative balance detected: ${metric.valor}`,
        recommendation: 'Consider showing balance alerts or overdraft notifications',
        birhausPrinciple: 6 // Progressive disclosure
      })
    }

    // Alert for exceeded spending goals
    if (metric.objetivo && metric.valor > metric.objetivo.valor) {
      const overage = ((metric.valor - metric.objetivo.valor) / metric.objetivo.valor) * 100
      reportViolation({
        type: 'performance',
        severity: overage > 50 ? 'error' : 'warning',
        element: document.createElement('div'),
        message: `Metric exceeds goal by ${overage.toFixed(1)}%`,
        messageEs: `Métrica supera objetivo en ${overage.toFixed(1)}%`,
        messageEn: `Metric exceeds goal by ${overage.toFixed(1)}%`,
        recommendation: 'Show clear goal progress and overage warnings',
        birhausPrinciple: 6 // Progressive disclosure
      })
    }

    // Alert for significant negative trends
    if (metric.tendencia && metric.tendencia.direccion === 'bajada' && Math.abs(metric.tendencia.porcentaje) > 20) {
      reportViolation({
        type: 'performance',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Significant downward trend: ${metric.tendencia.porcentaje}%`,
        messageEs: `Tendencia descendente significativa: ${metric.tendencia.porcentaje}%`,
        messageEn: `Significant downward trend: ${metric.tendencia.porcentaje}%`,
        recommendation: 'Consider highlighting concerning financial trends',
        birhausPrinciple: 6 // Progressive disclosure
      })
    }
  }, [metric, reportViolation])

  const typeConfig = metric.categoria ? metricTypeConfig[metric.categoria] : metricTypeConfig.balance
  const trendInfo = metric.tendencia ? trendConfig[metric.tendencia.direccion] : null

  // Calculate goal progress percentage
  const goalProgress = React.useMemo(() => {
    if (!metric.objetivo || metric.objetivo.valor === 0) return 0
    return Math.min(100, (metric.valor / metric.objetivo.valor) * 100)
  }, [metric.valor, metric.objetivo])

  // Determine if metric is concerning
  const isConcerning = React.useMemo(() => {
    if (metric.categoria === 'balance' && metric.valor < 0) return true
    if (metric.objetivo && metric.valor > metric.objetivo.valor) return true
    if (metric.tendencia && metric.tendencia.direccion === 'bajada' && Math.abs(metric.tendencia.porcentaje) > 20) return true
    return false
  }, [metric])

  // Format display value
  const formatDisplayValue = (value: number, currency: string = 'PYG') => {
    if (isPrivacyEnabled) {
      return '••••••••'
    }
    return <BirhausCurrencyDisplay amount={value} currency={currency as any} />
  }

  // Toggle privacy mode
  const togglePrivacy = React.useCallback(() => {
    setIsPrivacyEnabled(!isPrivacyEnabled)
  }, [isPrivacyEnabled])

  // Get size classes
  const sizeClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8'
  }

  // Get variant classes
  const variantClasses = {
    default: 'bg-white border border-birhaus-border',
    outline: 'bg-transparent border-2 border-birhaus-border',
    filled: 'bg-birhaus-muted border border-birhaus-border',
    gradient: 'bg-gradient-to-br from-white to-birhaus-muted/20 border border-birhaus-border'
  }

  return (
    <div
      className={cn(
        'rounded-lg transition-all duration-200 hover:shadow-md',
        sizeClasses[size || 'medium'],
        variantClasses[variant || 'default'],
        isConcerning && 'ring-2 ring-red-200 border-red-200',
        className
      )}
      {...props}
    >
      {/* Header with icon and privacy toggle */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-full', typeConfig.colorClass)}>
            {typeConfig.icon}
          </div>
          <div>
            <h3 className="font-semibold text-birhaus-foreground">
              {language === 'es' ? typeConfig.nameEs : typeConfig.nameEn}
            </h3>
            {metric.descripcionEs && (
              <p className="text-sm text-birhaus-muted-foreground mt-1">
                {metric.descripcionEs}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Warning indicator */}
          {isConcerning && (
            <div 
              className="text-red-600"
              title={language === 'es' ? 'Atención requerida' : 'Attention required'}
            >
              <AlertTriangle size={16} />
            </div>
          )}

          {/* Privacy toggle */}
          {allowPrivacyToggle && (
            <button
              onClick={togglePrivacy}
              className="p-1 hover:bg-birhaus-muted rounded transition-colors"
              aria-label={isPrivacyEnabled 
                ? (language === 'es' ? 'Mostrar valor' : 'Show value')
                : (language === 'es' ? 'Ocultar valor' : 'Hide value')}
            >
              {isPrivacyEnabled ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Main value */}
      <div className="mb-4">
        <div className="text-2xl font-bold text-birhaus-foreground mb-1">
          {formatDisplayValue(metric.valor, metric.moneda)}
        </div>
        
        {/* Trend indicator */}
        {showTrend && metric.tendencia && trendInfo && !isPrivacyEnabled && (
          <div className={cn('flex items-center gap-2 text-sm', trendInfo.colorClass)}>
            {trendInfo.icon}
            <span>
              {metric.tendencia.porcentaje > 0 ? '+' : ''}{metric.tendencia.porcentaje.toFixed(1)}%
            </span>
            <span className="text-birhaus-muted-foreground">
              {metric.tendencia.periodo && (
                language === 'es' 
                  ? `vs. ${metric.tendencia.periodo}`
                  : `vs. ${metric.tendencia.periodo}`
              )}
            </span>
          </div>
        )}
      </div>

      {/* Goal progress */}
      {showGoalProgress && metric.objetivo && !isPrivacyEnabled && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-birhaus-muted-foreground">
              {language === 'es' ? 'Progreso del objetivo' : 'Goal progress'}
            </span>
            <span className="font-medium">
              {goalProgress.toFixed(0)}%
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-birhaus-muted rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-500',
                goalProgress >= 100 
                  ? 'bg-green-500' 
                  : goalProgress >= 80 
                  ? 'bg-blue-500'
                  : 'bg-yellow-500'
              )}
              style={{ width: `${Math.min(100, goalProgress)}%` }}
            />
          </div>
          
          {/* Goal target info */}
          <div className="flex items-center justify-between text-xs text-birhaus-muted-foreground mt-2">
            <span>
              {language === 'es' ? 'Meta: ' : 'Target: '}
              <BirhausCurrencyDisplay 
                amount={metric.objetivo.valor} 
                currency={metric.moneda as any}
              />
            </span>
            {metric.objetivo.fechaLimite && (
              <span>
                {language === 'es' ? 'Vence: ' : 'Due: '}
                {metric.objetivo.fechaLimite.toLocaleDateString(
                  language === 'es' ? 'es-PY' : 'en-US'
                )}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Additional info */}
      {metric.informacionAdicional && metric.informacionAdicional.length > 0 && (
        <div className="border-t border-birhaus-border pt-4">
          {metric.informacionAdicional.map((info, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-birhaus-muted-foreground mb-2 last:mb-0">
              <Info size={12} />
              <span>{info}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer with last update */}
      {metric.ultimaActualizacion && (
        <div className="mt-4 pt-4 border-t border-birhaus-border">
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Última actualización: ' : 'Last updated: '}
            {metric.ultimaActualizacion.toLocaleDateString(
              language === 'es' ? 'es-PY' : 'en-US',
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }
            )}
          </p>
        </div>
      )}
    </div>
  )
}

export type { BirhausFinancialMetricCardProps }